const TEAL = "#4FCDC4";
const TEXT = "#EDEDED";
const CARD_BORDER = "rgba(255,255,255,0.08)";

/**
 * Sidebar CTA card for blog posts.
 *
 * Opt-in via `sidebarCTA: "converter"` (or other key) on the post's exported
 * `post` object. Renders below the auto-TOC inside the same sticky container,
 * so the CTA is visible throughout the entire scroll on desktop. Subtle
 * treatment (dark surface, thin border, only the button is teal) so the
 * sidebar stays editorial rather than promotional.
 *
 * Logos are inline SVG components (not <img src>) so they are part of the
 * React component bundle, cannot 404, and stay visible across HMR refreshes.
 * Path data is the same as /public/icons/{google-ads,chatgpt}.svg.
 *
 * Extend with new CTA flavors by adding entries to CTA_CONFIGS.
 */

function GoogleAdsLogo({ size = 26 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Google Ads"
    >
      <path
        d="M3.9998 22.9291C1.7908 22.9291 0 21.1383 0 18.9293s1.7908-3.9998 3.9998-3.9998 3.9998 1.7908 3.9998 3.9998-1.7908 3.9998-3.9998 3.9998z"
        fill="#34A853"
      />
      <path
        d="M23.4641 16.9287L15.4632 3.072C14.3586 1.1587 11.9121.5028 9.9988 1.6074S7.4295 5.1585 8.5341 7.0718l8.0009 13.8567c1.1046 1.9133 3.5511 2.5679 5.4644 1.4646 1.9134-1.1046 2.568-3.5511 1.4647-5.4644z"
        fill="#4285F4"
      />
      <path
        d="M7.5137 4.8438L1.5645 15.1484A4.5 4.5 0 0 1 4 14.4297c2.5597-.0075 4.6248 2.1585 4.4941 4.7148l3.2168-5.5723-3.6094-6.25c-.4499-.7793-.6322-1.6394-.5878-2.4784z"
        fill="#FBBC04"
      />
    </svg>
  );
}

function ChatGPTLogo({ size = 26 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 250 250"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="ChatGPT"
    >
      <path
        d="M94.9082 93.1534V69.8036C94.9082 67.837 95.6463 66.3617 97.3659 65.3793L144.313 38.3432C150.703 34.6566 158.323 32.937 166.187 32.937C195.68 32.937 214.362 55.7956 214.362 80.1274C214.362 81.8476 214.362 83.8141 214.115 85.7806L165.449 57.2688C162.5 55.5487 159.549 55.5487 156.6 57.2688L94.9082 93.1534ZM204.529 184.095V128.3C204.529 124.858 203.053 122.401 200.105 120.681L138.413 84.7961L158.567 73.2433C160.287 72.2613 161.762 72.2613 163.483 73.2433L210.429 100.28C223.948 108.146 233.041 124.858 233.041 141.08C233.041 159.759 221.981 176.964 204.529 184.093V184.095ZM80.4069 134.938L60.2524 123.141C58.5328 122.159 57.7947 120.683 57.7947 118.717V64.6442C57.7947 38.3453 77.9491 18.4352 105.232 18.4352C115.556 18.4352 125.14 21.8775 133.253 28.0214L84.8332 56.0425C81.8847 57.7621 80.4095 60.2199 80.4095 63.6617V134.94L80.4069 134.938ZM123.789 160.008L94.9082 143.786V109.377L123.789 93.156L152.667 109.377V143.786L123.789 160.008ZM142.346 234.728C132.022 234.728 122.438 231.286 114.325 225.142L162.744 197.121C165.693 195.402 167.169 192.944 167.169 189.501V118.223L187.57 130.021C189.29 131.003 190.028 132.478 190.028 134.444V188.517C190.028 214.816 169.626 234.726 142.346 234.726V234.728ZM84.0936 179.918L37.1469 152.881C23.6276 145.015 14.5347 128.303 14.5347 112.082C14.5347 93.156 25.8411 76.1969 43.2908 69.0685V125.108C43.2908 128.549 44.7666 131.007 47.7151 132.727L109.163 168.365L89.0086 179.918C87.289 180.9 85.8132 180.9 84.0936 179.918ZM81.3914 220.227C53.6173 220.227 33.2164 199.335 33.2164 173.527C33.2164 171.56 33.4628 169.594 33.7072 167.627L82.127 195.648C85.0755 197.368 88.0266 197.368 90.9751 195.648L152.667 160.01V183.36C152.667 185.327 151.93 186.802 150.21 187.784L103.263 214.82C96.8727 218.507 89.2529 220.227 81.3889 220.227H81.3914ZM142.346 249.474C172.086 249.474 196.909 228.337 202.565 200.317C230.092 193.188 247.789 167.38 247.789 141.082C247.789 123.876 240.416 107.164 227.143 95.12C228.372 89.958 229.11 84.7961 229.11 79.6367C229.11 44.4897 200.598 18.1888 167.662 18.1888C161.027 18.1888 154.636 19.1708 148.246 21.3842C137.184 10.5691 121.945 3.6875 105.232 3.6875C75.4919 3.6875 50.6688 24.8239 45.0135 52.8445C17.4858 59.973 -0.210938 85.7806 -0.210938 112.079C-0.210938 129.285 7.16187 145.997 20.4348 158.041C19.2059 163.203 18.4682 168.365 18.4682 173.524C18.4682 208.671 46.9801 234.972 79.9157 234.972C86.5508 234.972 92.9416 233.99 99.3325 231.777C110.392 242.592 125.631 249.474 142.346 249.474Z"
        fill={TEXT}
      />
    </svg>
  );
}

const CTA_CONFIGS = {
  converter: {
    title: "Convert Search Ads to Conversation Ads",
    href: "/tools/google-ads-to-chatgpt-ads-converter",
    LeftLogo: GoogleAdsLogo,
    RightLogo: ChatGPTLogo,
    buttonLabel: "Try Free",
  },
};

export default function BlogSidebarCTA({ type }) {
  const config = CTA_CONFIGS[type];
  if (!config) return null;
  const { LeftLogo, RightLogo } = config;

  return (
    <aside
      aria-label="Free tool"
      style={{
        marginTop: 24,
        padding: "16px 14px",
        borderRadius: 12,
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${CARD_BORDER}`,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: TEXT,
          fontFamily: "var(--display)",
          lineHeight: 1.35,
          marginBottom: 14,
        }}
      >
        {config.title}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <LeftLogo />
        <span
          aria-hidden="true"
          style={{ fontFamily: "var(--mono)", fontSize: 18, color: TEAL, lineHeight: 1 }}
        >
          &rarr;
        </span>
        <RightLogo />
      </div>

      <a
        href={config.href}
        style={{
          display: "block",
          textAlign: "center",
          padding: "10px 16px",
          borderRadius: 10,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
          fontWeight: 600,
          fontSize: 13,
          fontFamily: "var(--body)",
          lineHeight: 1.4,
        }}
      >
        {config.buttonLabel}
      </a>
    </aside>
  );
}
